import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import {
  type AdminApproval,
  AdminApprovalDecision,
  AdminApprovalType,
  adminapprovalReviewAdminApprovalMutation,
} from '@column/api';

import { invalidateAdminApprovals } from '~/lib/approvals/queries';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

const actionSubjects: Record<AdminApprovalType, string> = {
  [AdminApprovalType.USER_INVITE]: 'invite',
  [AdminApprovalType.USER_ROLE_ASSIGN]: 'role change',
  [AdminApprovalType.ROLE_UPDATE]: 'change',
  [AdminApprovalType.PLATFORM_UPDATE_REQUIRE_USER_CHANGE_APPROVAL]: 'setting change',
};

export const getReviewQueue = (approvals: AdminApproval[], startId?: string) => {
  const start = approvals.find((approval) => approval.id === startId);
  const queue = start && !start.can_review ? [start] : approvals.filter((approval) => approval.can_review);
  return {
    queue,
    startIndex: Math.max(
      queue.findIndex((approval) => approval.id === startId),
      0
    ),
  };
};

const useCancelAdminApproval = () => {
  const queryClient = useQueryClient();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { mutate } = useMutation({
    ...adminapprovalReviewAdminApprovalMutation(),
    onSuccess: () => {
      invalidateAdminApprovals(queryClient);
      addSuccessNotification({ content: 'Request cancelled' });
    },
    onError: (error: { message?: string }) => {
      addDangerNotification({ content: error?.message || 'Failed to cancel request' });
    },
  });
  return useCallback(
    (approvalId: string) =>
      mutate({ path: { admin_approval_id: approvalId }, body: { decision: AdminApprovalDecision.REJECTED } }),
    [mutate]
  );
};

export const useAdminApprovalReview = (approvals: AdminApproval[]) => {
  const { currentUser } = useSessionStore();
  const [reviewStartId, setReviewStartId] = useState<string>();
  const cancelApproval = useCancelAdminApproval();
  const firstReviewable = useMemo(() => approvals.find((approval) => approval.can_review), [approvals]);
  const { queue, startIndex } = useMemo(() => getReviewQueue(approvals, reviewStartId), [approvals, reviewStartId]);

  const openReview = useCallback((approval: AdminApproval) => setReviewStartId(approval.id), []);
  const closeReview = useCallback(() => setReviewStartId(undefined), []);

  const getApprovalActions = useCallback(
    (approval: AdminApproval) => {
      const subject = actionSubjects[approval.type];
      if (approval.can_review) return [{ label: `Review ${subject}`, onClick: () => openReview(approval) }];
      if (approval.requested_by.id === currentUser?.id) {
        return [{ label: `Cancel ${subject}`, onClick: () => cancelApproval(approval.id) }];
      }
      return [];
    },
    [cancelApproval, currentUser?.id, openReview]
  );

  return {
    openReview,
    getApprovalActions,
    startReview: firstReviewable ? () => openReview(firstReviewable) : undefined,
    dialogProps: { approvals: queue, startIndex, isOpen: !!reviewStartId, onClose: closeReview },
  };
};
